import type {
  AudienceIn,
  CreateLaunch,
  CreateSurvey,
  EditTurf,
  GeocodeRequest,
  LaunchTurfInput,
  NewTurf,
  TurfAssignmentChange,
  UpdateLaunch,
  UpdateSurvey,
  CanvassSummaryParams
} from "@doors/api-client";
import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueries,
  useQuery
} from "@tanstack/preact-query";
import { FetchResponse } from "openapi-fetch";
import { fetchClient } from "./api-client";

// the fetch client (correctly) reports that when api calls fall they don't return
// any data. this wrapper handles that case and narrows the type so that usage
// is more ergonomic in calling components. tanstack-query will retry on throw, so
// none of these exceptions become user visible.
//
// if you are seeing that your data is possibly undefined in a component make sure
// to check for (isSuccess || (!isPending && !isError)) before referencing the data
async function wrapFetch<
  R extends Record<string | number, any>,
  O,
  M extends `${string}/${string}`
>(apiFetch: Promise<FetchResponse<R, O, M>>) {
  const { error, data } = await apiFetch;
  if (error) throw error;
  if (data == null) throw new Error("unexpectedly no data");
  return data;
}

export const useCheckUsername = (username: string, isEnabled: boolean = true) =>
  useQuery(
    queryOptions({
      queryKey: ["username-avail", username],
      queryFn: () =>
        wrapFetch(
          fetchClient.GET("/api/v1/user/username-available", {
            params: { query: { username } }
          })
        ),
      enabled: isEnabled && !!username
    })
  );

export const useMembers = () =>
  useQuery(
    queryOptions({
      queryKey: ["members"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/user", {}))
    })
  );

export const useAudiences = () =>
  useQuery(
    queryOptions({
      queryKey: ["audiences"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/audiences", {}))
    })
  );

export const useCreateAudience = () =>
  useMutation(
    mutationOptions({
      mutationFn: (body: AudienceIn) =>
        wrapFetch(fetchClient.POST("/api/v1/audiences", { body })),
      meta: {
        invalidates: [["audiences"]]
      }
    })
  );

export const useModifyAudience = (audience_id: number) =>
  useMutation(
    mutationOptions({
      mutationFn: (body: AudienceIn) =>
        wrapFetch(
          fetchClient.PUT(`/api/v1/audiences/${audience_id}`, {
            params: { path: { aud_id: audience_id } },
            body
          })
        ),
      meta: {
        invalidates: [["audiences"], ["audience-count", audience_id]]
      }
    })
  );

export const useAudienceCount = (aud_id: number | null) =>
  useQuery(
    queryOptions({
      queryKey: ["audience-count", aud_id],
      queryFn: () =>
        wrapFetch(
          fetchClient.GET(`/api/v1/audiences/${aud_id ?? 0}/counts`, {
            params: { path: { aud_id: aud_id ?? 0 } }
          })
        ),
      staleTime: Infinity, // this can take ~5s, don't do it so often
      enabled: !!aud_id
    })
  );

export const usePartyAffiliates = () =>
  useQuery(
    queryOptions({
      queryKey: ["parties"],
      queryFn: () =>
        wrapFetch(fetchClient.GET("/api/v1/demographics/parties", {})),
      staleTime: "static"
    })
  );

export const useCounties = () =>
  useQuery(
    queryOptions({
      queryKey: ["counties"],
      queryFn: () =>
        wrapFetch(fetchClient.GET("/api/v1/districts/counties", {})),
      staleTime: "static"
    })
  );

export const usePrecincts = () =>
  useQuery(
    queryOptions({
      queryKey: ["precincts"],
      queryFn: () =>
        wrapFetch(fetchClient.GET("/api/v1/districts/precincts", {})),
      staleTime: "static"
    })
  );

// Query for each precinct's geocoded doors
export const usePrecinctDoors = (precincts: string[], audience_id: number) =>
  useQueries({
    queries: precincts
      .filter((p) => !!p)
      .map((p) =>
        queryOptions({
          queryKey: ["precinct-doors", p, audience_id],
          queryFn: () =>
            wrapFetch(
              fetchClient.GET(`/api/v1/districts/precincts/${p}/doors`, {
                params: { path: { precinct: p }, query: { aud: audience_id } }
              })
            ),
          enabled: !!audience_id
        })
      )
  });

// Query for and cache each county's precinct boundaries separately
// rather than asking for all the geometries at once, they are large!
export const useCountyGeometryQuery = (counties: string[]) =>
  useQueries({
    queries: counties.map((county) =>
      queryOptions({
        queryKey: ["county-precinct-geos", county],
        queryFn: () =>
          wrapFetch(
            fetchClient.POST("/api/v1/districts/counties/query-geometry", {
              body: { counties: [county] }
            })
          ),
        staleTime: Infinity
      })
    )
  });

export const useGmapsKey = () =>
  useQuery(
    queryOptions({
      queryKey: ["goog-maps-key"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/values/gmaps", {})),
      staleTime: "static"
    })
  );

// server-provided org info, including the public origin used to build launch
// page links
export const useServerVersion = () =>
  useQuery(
    queryOptions({
      queryKey: ["server-version"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/", {})),
      staleTime: "static"
    })
  );

export const useGeocodePrecincts = () =>
  useMutation(
    mutationOptions({
      mutationKey: ["geocode-precinct"],
      mutationFn: (body: GeocodeRequest) =>
        wrapFetch(fetchClient.POST("/api/v1/geocoding", { body })),
      meta: {
        invalidates: [["geocode-queue"], ["precinct-doors"]]
      }
    })
  );

export const useGeocodingStats = () =>
  useQuery(
    queryOptions({
      queryKey: ["geocode-queue"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/geocoding", {})),
      refetchInterval: (query) => {
        if (query.state.data?.queued === 0) return false;
        return 5_000;
      }
    })
  );

export const useSurveys = () =>
  useQuery(
    queryOptions({
      queryKey: ["surveys"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/surveys", {})),
      staleTime: 30_000
    })
  );

export const useCreateSurvey = () =>
  useMutation(
    mutationOptions({
      mutationFn: (body: CreateSurvey) =>
        wrapFetch(fetchClient.POST("/api/v1/surveys", { body })),
      meta: {
        invalidates: [["surveys"]]
      }
    })
  );

export const useUpdateSurvey = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({ id, body }: { id: number; body: UpdateSurvey }) =>
        wrapFetch(
          fetchClient.PUT(`/api/v1/surveys/${id}`, {
            params: { path: { id } },
            body
          })
        ),
      meta: {
        invalidates: [["surveys"]]
      }
    })
  );

export const useDeleteSurvey = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({ id }: { id: number }) =>
        wrapFetch(
          fetchClient.DELETE(`/api/v1/surveys/${id}`, {
            params: { path: { id } }
          })
        ),
      meta: {
        invalidates: [["surveys"]]
      }
    })
  );

export const useTurfs = (launch?: number | null, enabled = true) =>
  useQuery(
    queryOptions({
      queryKey: ["turfs", launch],
      queryFn: () =>
        wrapFetch(
          fetchClient.GET("/api/v1/turf", {
            params: { query: { launch: launch ?? undefined } }
          })
        ),
      enabled
    })
  );

export const useNewTurf = () =>
  useMutation(
    mutationOptions({
      mutationFn: (body: NewTurf) =>
        wrapFetch(fetchClient.POST("/api/v1/turf", { body })),
      meta: {
        invalidates: [["turfs"]]
      }
    })
  );

export const useUpdateTurf = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({ id, turf }: { id: number; turf: EditTurf }) =>
        wrapFetch(
          fetchClient.POST(`/api/v1/turf/${id}`, {
            params: { path: { turf_id: id } },
            body: turf
          })
        ),
      meta: {
        invalidates: [["turfs"]]
      }
    })
  );

export const useDeleteTurf = () =>
  useMutation(
    mutationOptions({
      mutationFn: (id: number) =>
        wrapFetch(
          fetchClient.DELETE(`/api/v1/turf/${id}`, {
            params: { path: { turf_id: id } }
          })
        ),
      meta: {
        invalidates: [["turfs"]]
      }
    })
  );

export const useAssignTurf = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({
        turfId,
        changes
      }: {
        turfId: number;
        changes: TurfAssignmentChange;
      }) =>
        wrapFetch(
          fetchClient.POST(`/api/v1/turf/${turfId}/assign`, {
            params: { path: { turf_id: turfId } },
            body: changes
          })
        ),
      meta: {
        invalidates: [["turfs"]]
      }
    })
  );

export const useLaunches = () =>
  useQuery(
    queryOptions({
      queryKey: ["launches"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/launches", {}))
    })
  );

export const useNewLaunch = () =>
  useMutation(
    mutationOptions({
      mutationFn: (body: CreateLaunch) =>
        wrapFetch(fetchClient.POST("/api/v1/launches", { body })),
      meta: {
        invalidates: [["launches"]]
      }
    })
  );

export const useUpdateLaunch = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({ id, launch }: { id: number; launch: UpdateLaunch }) =>
        wrapFetch(
          fetchClient.PUT(`/api/v1/launches/${id}`, {
            params: { path: { id } },
            body: launch
          })
        ),
      meta: {
        invalidates: [["launches"]]
      }
    })
  );

export const useDeleteLaunch = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({ id }: { id: number }) =>
        wrapFetch(
          fetchClient.DELETE(`/api/v1/launches/${id}`, {
            params: { path: { id } }
          })
        ),
      meta: {
        invalidates: [["launches"]]
      }
    })
  );

export const useScheduleTurf = () =>
  useMutation(
    mutationOptions({
      mutationFn: ({
        id,
        addRemove
      }: {
        id: number;
        addRemove: LaunchTurfInput;
      }) =>
        wrapFetch(
          fetchClient.POST(`/api/v1/launches/${id}/turf`, {
            params: { path: { launch_id: id } },
            body: addRemove
          })
        ),
      meta: {
        invalidates: [["launches"], ["turfs"]]
      }
    })
  );

export const useLaunchProgress = (
  launchId: number | null,
  refetchInterval: number | false
) =>
  useQuery(
    queryOptions({
      queryKey: ["launch-progress", launchId],
      queryFn: () =>
        wrapFetch(
          fetchClient.GET(`/api/v1/launches/${launchId ?? 0}/progress`, {
            params: { path: { launch_id: launchId ?? 0 } }
          })
        ),
      enabled: !!launchId,
      refetchInterval
    })
  );

export const useTelemetry = () =>
  useQuery(
    queryOptions({
      queryKey: ["telemetry"],
      queryFn: () => wrapFetch(fetchClient.GET("/api/v1/telemetry")),
      refetchInterval: 5_000
    })
  );

// Reports are read-only aggregates: each report type has its own endpoint and
// its own result shape
export const useCanvassSummary = (params: CanvassSummaryParams | null) =>
  useQuery(
    queryOptions({
      queryKey: ["reports", "canvass-summary", params],
      queryFn: () =>
        wrapFetch(
          fetchClient.POST("/api/v1/reports/canvass-summary", {
            body: params!
          })
        ),
      enabled: params !== null
    })
  );

export const useVoterLists = () =>
  useQuery(
    queryOptions({
      queryKey: ["voter-lists"],
      queryFn: () =>
        wrapFetch(fetchClient.GET("/api/v1/external-data/voter-lists"))
    })
  );
